import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, hasFxAccess } from '../auth/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import UserMenu from './UserMenu';
import './TopBanner.css';

const TopBanner: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="top-banner">
      <div className="banner-left">
        <div className="logo-container">
          <img src="/palace.jpg" alt="Palace Technology" className="palace-logo" />
        </div>
      </div>

      <div className="banner-right">
        <div className="app-title">
          <img src="/fx-plus-logo.svg" alt="FX+" className="app-logo" />
        </div>
        <Link to="/" className="home-icon-link" title={t('navigation.home', 'Home')}>
          <svg width="28" height="28" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="home-icon">
            <path d="M10 2.5L2 9.5H5V17H8.5V12.5H11.5V17H15V9.5H18L10 2.5Z" fill="currentColor"/>
          </svg>
        </Link>
        <LanguageSwitcher />
        <div className="navigation-menu" ref={menuRef}>
          <button 
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ☰
          </button>
          {menuOpen && (
            <div className="menu-dropdown">
              {/* ── FX Section ── only shown when user has FX access */}
              {hasFxAccess(user) && (
                <>
                  {/* FX section header only shown when both FX and Commodities are present */}
                  {user?.role && user.role !== 'bank_admin' && user?.moduleRoles?.Commodities && (
                    <div className="menu-section-header">{t('navigation.fxSection')}</div>
                  )}

                  {/* Trade Booking - Available to client_admin, client_fo_user, client_fo_bo_user, client_ro_user, client_acc_user */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/client/trade-booking"
                      className={`menu-item ${location.pathname === '/client/trade-booking' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.tradeBooking')}
                    </Link>
                  )}

                  {/* Confirmations - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/confirmations"
                      className={`menu-item ${location.pathname === '/confirmations' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.confirmations')}
                    </Link>
                  )}

                  {/* Contracts - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/contract-comparison"
                      className={`menu-item ${location.pathname === '/contract-comparison' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.contracts')}
                    </Link>
                  )}

                  {/* Valuation - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/valuation"
                      className={`menu-item ${location.pathname === '/valuation' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.valuation')}
                    </Link>
                  )}

                  {/* Forward Expiries - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/forward-maturity"
                      className={`menu-item ${location.pathname === '/forward-maturity' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.forwardExpiries')}
                    </Link>
                  )}

                  {/* Cashflow Management - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/cashflow-management"
                      className={`menu-item ${location.pathname === '/cashflow-management' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.cashflowManagement')}
                    </Link>
                  )}

                  {/* Accounting - Available to client_admin, client_bo_user, client_fo_bo_user, client_ro_user, client_acc_user */}
                  {(user?.role === 'client_admin' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/accounting"
                      className={`menu-item ${location.pathname === '/accounting' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.accounting')}
                    </Link>
                  )}

                  {/* Modifications - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <Link
                      to="/modifications"
                      className={`menu-item ${location.pathname === '/modifications' ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('navigation.modifications')}
                    </Link>
                  )}

                  <div className="menu-divider" />

                  {/* Documents & Reports (consolidated) - Available to all client roles */}
                  {(user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user' || user?.role === 'client_ro_user' || user?.role === 'client_acc_user') && (
                    <div className="menu-item-with-submenu">
                      <div className={`menu-item ${location.pathname === '/custom-documents' || location.pathname.startsWith('/reports') ? 'active' : ''}`}>
                        {t('navigation.documentsAndReports')}
                      </div>
                      <div className="submenu">
                        <Link
                          to="/custom-documents"
                          className={`submenu-item ${location.pathname === '/custom-documents' ? 'active' : ''}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {t('navigation.customDocuments')}
                        </Link>
                        <Link
                          to="/reports/custom-reports"
                          className={`submenu-item ${location.pathname === '/reports/custom-reports' ? 'active' : ''}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {t('navigation.customReports', 'Custom Reports')}
                        </Link>
                        <Link
                          to="/reports/positions"
                          className={`submenu-item ${location.pathname === '/reports/positions' ? 'active' : ''}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {t('navigation.positions')}
                        </Link>
                        <Link
                          to="/reports/confirmations"
                          className={`submenu-item ${location.pathname === '/reports/confirmations' ? 'active' : ''}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {t('navigation.confirmationsReport')}
                        </Link>
                        <Link
                          to="/reports/accounting"
                          className={`submenu-item ${location.pathname === '/reports/accounting' ? 'active' : ''}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {t('navigation.accountingReport', 'Accounting')}
                        </Link>
                      </div>
                    </div>
                  )}

                  <div className="menu-divider" />

                  {/* Administration - Available to client_admin (all tabs) and client_acc_user (accounting tab only) */}
                  {(user?.role === 'client_admin' || user?.role === 'client_acc_user') && (
                    <div className="menu-item-with-submenu">
                      <div className={`menu-item ${location.pathname === '/admin' ? 'active' : ''}`}>
                        {t('navigation.administration')}
                      </div>
                      <div className="submenu">
                        {[
                          { tab: 'confirmations',         labelKey: 'admin.tabs.confirmations' },
                          { tab: 'accounts',              labelKey: 'admin.tabs.accounts' },
                          { tab: 'workflow',              labelKey: 'admin.tabs.workflow' },
                          { tab: 'fxgo',                  labelKey: 'admin.tabs.fxgo' },
                          { tab: 'email',                 labelKey: 'admin.tabs.email' },
                          { tab: 'accounting',            labelKey: 'admin.tabs.accounting' },
                          { tab: 'groupings',             labelKey: 'admin.tabs.groupings' },
                          { tab: 'imagecapture',          labelKey: 'admin.tabs.imagecapture' },
                          { tab: 'customdocuments',       labelKey: 'admin.tabs.customdocuments' },
                          { tab: 'contracts',             labelKey: 'admin.tabs.contracts' },
                          { tab: 'settlementinstructions',labelKey: 'admin.tabs.settlementinstructions' },
                          { tab: 'forwardexpiries',       labelKey: 'admin.tabs.forwardExpiries' },
                          { tab: 'users',                 labelKey: 'admin.tabs.users' },
                          { tab: 'valuation',             labelKey: 'admin.tabs.valuation' },
                        ].filter(({ tab }) => {
                          if (user?.role === 'client_acc_user') return tab === 'accounting';
                          return true;
                        }).map(({ tab, labelKey }) => {
                          const defaultTab = user?.role === 'client_acc_user' ? 'accounting' : 'confirmations';
                          const currentTab = new URLSearchParams(location.search).get('tab') ?? defaultTab;
                          const isActive = location.pathname === '/admin' && currentTab === tab;
                          return (
                            <Link
                              key={tab}
                              to={`/admin?tab=${tab}`}
                              className={`submenu-item ${isActive ? 'active' : ''}`}
                              onClick={() => setMenuOpen(false)}
                            >
                              {t(labelKey)}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Commodities Section ── (visible when user has Commodities module role) */}
              {user?.moduleRoles?.Commodities && (
                <>
                  {/* Section header only shown when both FX and Commodities are present */}
                  {hasFxAccess(user) && user?.role && user.role !== 'bank_admin' && (
                    <div className="menu-section-header">{t('navigation.commoditiesSection')}</div>
                  )}
                  {/* 1. Registro de Operaciones */}
                  <Link
                    to="/commodities/trade-booking"
                    className={`menu-item ${location.pathname === '/commodities/trade-booking' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.commodityTradeBooking')}
                  </Link>
                  {/* 2. Valorización */}
                  <Link
                    to="/commodities/valuation"
                    className={`menu-item ${location.pathname === '/commodities/valuation' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.commodityValuation')}
                  </Link>
                  {/* 3. Vencimientos */}
                  <Link
                    to="/commodities/expiry"
                    className={`menu-item ${location.pathname === '/commodities/expiry' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.commodityExpiry')}
                  </Link>
                  {/* 4. Approvals & Modifications (KAN-307) */}
                  <Link
                    to="/commodities/modifications"
                    className={`menu-item ${location.pathname === '/commodities/modifications' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.commodityModifications')}
                  </Link>

                  <div className="menu-divider" />

                  {/* 3. Documentos & Reportes → Posiciones, Reportes Personalizados */}
                  <div className="menu-item-with-submenu">
                    <div className={`menu-item ${['/commodities/positions', '/commodities/custom-reports'].includes(location.pathname) ? 'active' : ''}`}>
                      {t('navigation.documentsAndReports')}
                    </div>
                    <div className="submenu">
                      <Link
                        to="/commodities/custom-reports"
                        className={`submenu-item ${location.pathname === '/commodities/custom-reports' ? 'active' : ''}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {t('navigation.customReports', 'Custom Reports')}
                      </Link>
                      <Link
                        to="/commodities/positions"
                        className={`submenu-item ${location.pathname === '/commodities/positions' ? 'active' : ''}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {t('navigation.positions')}
                      </Link>
                    </div>
                  </div>

                  <div className="menu-divider" />

                  {/* 4. Administración → Captura de Operaciones, Captura de Vencimientos */}
                  {user?.role === 'client_admin' && (
                    <div className="menu-item-with-submenu">
                      <div className={`menu-item ${location.pathname === '/commodities/admin' ? 'active' : ''}`}>
                        {t('navigation.administration')}
                      </div>
                      <div className="submenu">
                        {(['subscriptionImportConfigs', 'expiryImportConfigs'] as const).map((tab) => {
                          const currentTab = new URLSearchParams(location.search).get('tab') ?? 'subscriptionImportConfigs';
                          const isActive = location.pathname === '/commodities/admin' && currentTab === tab;
                          return (
                            <Link
                              key={tab}
                              to={`/commodities/admin?tab=${tab}`}
                              className={`submenu-item ${isActive ? 'active' : ''}`}
                              onClick={() => setMenuOpen(false)}
                            >
                              {t(`commodities.admin.tabs.${tab}`)}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Bank Admin Panel - Only for bank_admin */}
              {user?.role === 'bank_admin' && (
                <>
                  <Link
                    to="/bank"
                    className={`menu-item ${location.pathname === '/bank' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.bankAdmin')}
                  </Link>
                  <Link
                    to="/fx-analytics"
                    className={`menu-item ${location.pathname === '/fx-analytics' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('navigation.fxAnalytics')}
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
        {user && <UserMenu user={user} />}
      </div>
    </div>
  );
};

export default TopBanner;